'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';

interface ShareMemoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShareMemoriesModal: React.FC<ShareMemoriesModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    postalCode: '',
    memory: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('https://formspree.io/f/manjrlgz', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for sharing your memory! Your submission has been received.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          postalCode: '',
          memory: ''
        });
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
          setSubmitMessage('');
        }, 2000);
      } else {
        setSubmitMessage('There was an error submitting your memory. Please try again.');
      }
    } catch {
      setSubmitMessage('There was an error submitting your memory. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Escape key press and prevent body scroll
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[999999] p-4">
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50 opacity-0 animate-fadeIn" style={{ animationDuration: '.5s', animationFillMode: 'forwards' }}></div>
      
      {/* Modal content with fade-in animation */}
      <div className="bg-white rounded-lg md:rounded-xl max-w-2xl w-full max-h-[calc(100vh-2rem)] md:max-h-[90vh] overflow-y-auto relative border border-gray-200 shadow-lg box-border opacity-0 animate-fadeIn" style={{ animationDuration: '.5s', animationFillMode: 'forwards' }}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-6 md:right-6 text-gray-500 hover:text-gray-700 z-10 w-8 h-8 md:w-auto md:h-auto flex items-center justify-center cursor-pointer"
        >
          <Image src="/x-close.svg" alt="Close" width={24} height={24} />
        </button>

        {/* Modal content */}
        <div className="p-4 md:p-11 pt-12 md:pt-6">
          {/* Header with icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center">
              <Image src="/shareupdated.svg" alt="Share" width={44} height={43} />
            </div>
          </div>

          {/* Title */}
          <h2
            style={{
              color: 'var(--text-brand, #005E60)',
              fontFamily: 'Sons Condensed',
              fontSize: '40px',
              fontStyle: 'normal',
              fontWeight: 800,
              lineHeight: 'normal',
              letterSpacing: '-0.012px',
              textTransform: 'uppercase',
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            Share Your Favourite Memories
          </h2>

          {/* Subtitle */}
          <p
            style={{
              color: 'var(--color-grey-900, #212121)',
              textAlign: 'center',
              fontFamily: 'Inter',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '28px',
              letterSpacing: '-0.005px',
              width: '559px',
              height: '56px',
              margin: '0 auto 2rem auto',
              display: 'block',
            }}
          >
            Share your story to help shape the site&apos;s remembrance in Peterborough
          </p>

          {/* Success/Error Message */}
          {submitMessage && (
            <div className={`text-center mb-6 p-4 rounded-lg ${
              submitMessage.includes('Thank you') 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {submitMessage}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First row - First name and Last name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005E60] focus:border-transparent placeholder-gray-400"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005E60] focus:border-transparent placeholder-gray-400"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Second row - Email and Postal Code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005E60] focus:border-transparent placeholder-gray-400"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <input
                  type="text"
                  name="postalCode"
                  placeholder="Postal Code"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005E60] focus:border-transparent placeholder-gray-400"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Textarea for memory */}
            <div>
              <textarea
                name="memory"
                placeholder="Your favourite memory"
                value={formData.memory}
                onChange={handleInputChange}
                rows={4}
                className="w-full p-4 border border-[#ACD2D2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005E60] focus:border-transparent placeholder-gray-400 resize-none"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Submit button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
                className={`${
                  isSubmitting 
                    ? 'bg-gray-300 cursor-not-allowed' 
                    : 'bg-[#C8FF08] hover:bg-[#002D2E] hover:text-white'
                } text-[#005E60] font-bold pt-2 pb-2 pr-2 pl-6 rounded-full text-xl transition duration-300 flex items-center justify-center gap-3 mx-auto min-w-[80px]`}
              >
                {isSubmitting ? 'Submitting...' : 'Share'}
                <span className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <Image 
                    src={isButtonHovered ? "/Arrow_hover.svg" : "/arrow.svg"} 
                    alt="Arrow" 
                    width={100} 
                    height={100} 
                  />
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ShareMemoriesModal; 